import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      // Fetch stats data for last 6 months
      const statsRef = collection(db, 'stats');
      const statsQuery = query(statsRef, orderBy('__name__', 'desc'), limit(6));
      const statsSnapshot = await getDocs(statsQuery);
      const incomesData = statsSnapshot.docs
        .map((doc) => ({
          name: doc.id,
          totalBalance: doc.data().totalBalance || 0,
          totalIncomes: doc.data().totalIncomes || 0,
        }))
        .reverse(); // Reverse to show oldest to newest

      // Fetch customers data
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customers = customersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Process accounts data for pie chart
      const accountsData = [
        {
          name: 'Active',
          value: customers.filter((customer) => customer.isActive).length,
        },
        {
          name: 'Inactive',
          value: customers.filter((customer) => !customer.isActive).length,
        },
      ];

      // Fetch users data
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch projects data
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projects = projectsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch time logs with date range filter
      let timeLogsQuery = collection(db, 'timeLogs');
      if (startDate && endDate) {
        // Convert dates to Firestore Timestamps
        const startTimestamp = new Date(startDate);
        const endTimestamp = new Date(endDate);
        endTimestamp.setHours(23, 59, 59, 999);

        timeLogsQuery = query(
          timeLogsQuery,
          where('date', '>=', startTimestamp),
          where('date', '<=', endTimestamp)
        );
      }
      const timeLogsSnapshot = await getDocs(timeLogsQuery);
      const timeLogs = timeLogsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Process hours by user with user names
      const hoursByUser = {};
      timeLogs.forEach((log) => {
        const user = users.find((u) => u.id === log.userId);
        const userName = user
          ? `${user.firstName} ${user.lastName}`
          : log.userId;

        if (!hoursByUser[userName]) {
          hoursByUser[userName] = {
            LUN: 0,
            MAR: 0,
            MER: 0,
            JEU: 0,
            VEN: 0,
            SAM: 0,
            DIM: 0,
          };
        }

        // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
        const logDate = log.date.toDate();
        const dayOfWeek = logDate.getDay();

        // Map day number to our day codes
        const dayMap = {
          1: 'LUN',
          2: 'MAR',
          3: 'MER',
          4: 'JEU',
          5: 'VEN',
          6: 'SAM',
          0: 'DIM',
        };

        const dayCode = dayMap[dayOfWeek];
        hoursByUser[userName][dayCode] += log.hours;
      });

      // Fetch sub-projects data
      const subProjectsSnapshot = await getDocs(collection(db, 'subProjects'));
      const subProjects = subProjectsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Process hours by project
      const hoursByProject = {};
      const totalHoursByProject = {};
      const budgetByProject = {};

      // First, get all time logs for total hours calculation
      const allTimeLogsQuery = collection(db, 'timeLogs');
      const allTimeLogsSnapshot = await getDocs(allTimeLogsQuery);
      const allTimeLogs = allTimeLogsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate metrics for each project
      projects.forEach((project) => {
        // Hours in date range
        const rangeTimeLogs = timeLogs.filter(
          (log) => log.projectId === project.id
        );
        const rangeHours = rangeTimeLogs.reduce(
          (total, log) => total + log.hours,
          0
        );

        // Calculate billable and non-billable hours in range
        const billableHours = rangeTimeLogs
          .filter((log) => log.isBillable)
          .reduce((total, log) => total + log.hours, 0);
        const nonBillableHours = rangeTimeLogs
          .filter((log) => !log.isBillable)
          .reduce((total, log) => total + log.hours, 0);

        // Total hours (all time)
        const allTimeLogsForProject = allTimeLogs.filter(
          (log) => log.projectId === project.id
        );
        const totalHours = allTimeLogsForProject.reduce(
          (total, log) => total + log.hours,
          0
        );

        // Calculate total budget from sub-projects
        const projectSubProjects = subProjects.filter(
          (sp) => sp.projectId === project.id
        );
        const totalBudget = projectSubProjects.reduce(
          (total, sp) => total + (sp.budgetHours || 0),
          0
        );

        // Store the metrics
        hoursByProject[project.name] = {
          rangeHours,
          totalHours,
          budget: totalBudget,
          isInternal: project.isInternal,
          billableHours,
          nonBillableHours,
        };
      });

      // Process projects with budget alerts
      const budgetAlerts = projects
        .filter((project) => {
          const projectTimeLogs = timeLogs.filter(
            (log) => log.projectId === project.id
          );
          const totalHours = projectTimeLogs.reduce(
            (total, log) => total + log.hours,
            0
          );
          const budgetPercentage =
            (totalHours / (project.budgetedHours || 1)) * 100;
          return budgetPercentage >= 80; // Alert if 80% or more of budget is used
        })
        .map((project) => {
          const projectTimeLogs = timeLogs.filter(
            (log) => log.projectId === project.id
          );
          const totalHours = projectTimeLogs.reduce(
            (total, log) => total + log.hours,
            0
          );
          return {
            name: project.name,
            budgetUsed: (totalHours / (project.budgetedHours || 1)) * 100,
          };
        });

      return {
        accountsData,
        hoursByUser,
        hoursByProject,
        budgetAlerts,
        incomesData,
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  accountsData: [],
  hoursByUser: {},
  hoursByProject: {},
  budgetAlerts: [],
  incomesData: [],
  loading: false,
  error: null,
  dateRange: {
    startDate: null,
    endDate: null,
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.accountsData = action.payload.accountsData;
        state.hoursByUser = action.payload.hoursByUser;
        state.hoursByProject = action.payload.hoursByProject;
        state.budgetAlerts = action.payload.budgetAlerts;
        state.incomesData = action.payload.incomesData;
        state.clientName = action.payload.clientName;
        state.isInternal = action.payload.isInternal;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setDateRange } = dashboardSlice.actions;
export default dashboardSlice.reducer;
